import Vue from 'vue';
import VueRouter, { isNavigationFailure, NavigationFailureType } from 'vue-router';
import guestRoutes from './routes/guest';
import clientRoutes from './routes/client';
import professionalRoutes from './routes/professional';
import store from '@/store';
import {
    GUEST_GUARD,
    CLIENT_GUARD,
    PROFESSIONAL_GUARD,
    CONFIG_SET_THEME,
    VALIDATION_CLEAR_BAGS,
    THEME_STORAGE_KEY,
    PROFILE_SET_LOCALE,
} from '@/constants';
import i18n, { localeOptions } from '@/languages';

Vue.use(VueRouter);

/*
    vue-router >= 3.1 returns a promise from push()/replace() that rejects when a
    navigation is aborted. Navigating to the route we are already on is not an
    error for us (guards and the 401 interceptor both redirect on their own), so
    swallow just that failure instead of leaking an unhandled rejection.
*/
['push', 'replace'].forEach((method) => {
    const original = VueRouter.prototype[method];
    VueRouter.prototype[method] = function (location, onComplete, onAbort) {
        if (onComplete || onAbort) {
            return original.call(this, location, onComplete, onAbort);
        }
        return original.call(this, location).catch((err) => {
            if (isNavigationFailure(err, NavigationFailureType.duplicated)) return err;
            return Promise.reject(err);
        });
    };
});

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        clientRoutes,
        professionalRoutes,
        guestRoutes,
        {
            path: '/bye',
            name: 'Bye',
            component: () => import('@/pages/Bye.vue'),
        },
        {
            path: '/error/:message/:retryPath',
            name: 'Error',
            component: () => import('@/pages/Error.vue'),
        },
        {
            path: '**',
            name: 'PageNotFound',
            component: () => import('@/pages/PageNotFound.vue'),
        },
        {
            path: '**',
            name: 'ProfileNotPublic',
            component: () => import('@/pages/ProfileNotPublic.vue'),
            props: true,
        },
        {
            path: '/landingPage',
            name: 'LandingPage',
            beforeEnter() {
                window.location.replace('https://www.myday.me/' + i18n.locale);
            },
        },
    ],
});

/*
    Update service worker on route changes in order
    to handle lazy loaded chunks from the new deploy
 */
router.beforeEach((_from, _to, next) => {
    // Deliberately not awaited: update() fetches the worker script over the
    // network, and navigation must not wait for that. Every promise still gets a
    // catch of its own, because an *unhandled* rejection is what was being
    // reported, not an uncaught one.
    navigator.serviceWorker
        ?.getRegistrations()
        .then((registrations) =>
            registrations
                // update() rejects with an InvalidStateError when there is no
                // active worker yet or an update is already installing.
                .filter((registration) => registration.active && !registration.installing)
                .forEach((registration) => registration.update().catch(console.warn)),
        )
        .catch(console.warn);

    next();
});

/*
    If path is prefixed by /{language code}/,
    update the locale and pop the language code from the path
*/
router.beforeEach((to, from, next) => {
    const supportedLanguages = localeOptions;

    let pathParts = to.fullPath.split('/');
    let pathPrefix = pathParts.length > 1 && pathParts[1];

    if (supportedLanguages.includes(pathPrefix)) {
        // Now pathPrefix is confirmed to be a language code
        i18n.locale = pathPrefix;
        document.documentElement.setAttribute('lang', pathPrefix);
        // Pop language code
        pathParts.splice(1, 1);

        store.commit(PROFILE_SET_LOCALE, pathPrefix);

        next({ path: pathParts.join('/'), query: to.query });
    } else {
        next();
    }
});

router.beforeEach((to, from, next) => {
    // Redirect to the home path of the previously used theme except when
    // navigating to the other home path intentionally
    const previousTheme = localStorage.getItem(THEME_STORAGE_KEY);

    if (to.name === 'ClientHome' && from.name !== 'ProfessionalHome') {
        if (previousTheme === 'professional') {
            next({ name: 'ProfessionalHome' });
            return;
        }
    }

    next();
    return;
});

router.beforeEach((to, from, next) => {
    store.commit(VALIDATION_CLEAR_BAGS);
    let theme = to.matched.find((m) => m.meta.theme)?.meta.theme;

    // if "to" is one of the next routes do not preserve previous theme
    const forceRoutesTheme = [...clientRoutes.children, ...guestRoutes.children]
        .filter(({ meta }) => meta && meta.forceRouteTheme)
        .map((route) => route.name);

    if (store.getters.isAuthenticated) {
        const previousTheme = localStorage.getItem(THEME_STORAGE_KEY);

        // don't allow user to access routes out of the scope of previous theme
        if (from.name === null && from.path === '/' && !forceRoutesTheme.includes(to.name)) {
            store.commit(CONFIG_SET_THEME, previousTheme);
            if (
                (theme && previousTheme && theme !== previousTheme) ||
                to.name === 'ClientAuthLogIn'
            ) {
                next({
                    name: store.getters.isThemeClient ? 'ClientCalendar' : 'ProfessionalCalendar',
                });
                return;
            }
        }
        store.commit(CONFIG_SET_THEME, theme ?? previousTheme);
        if (to.meta.guard === GUEST_GUARD) {
            next({ name: store.getters.isThemeClient ? 'ClientCalendar' : 'ProfessionalCalendar' });
            return;
        }
    } else {
        if (to.meta.guard === CLIENT_GUARD) {
            next({ name: 'ClientAuthLogIn', query: { redirect: to.fullPath } });
            return;
        }

        if (to.meta.guard === PROFESSIONAL_GUARD) {
            next({ name: 'ProfessionalAuthLogIn', query: { redirect: to.fullPath } });
            return;
        }
    }
    next();
});

router.beforeEach((to, from, next) => {
    if (to.matched.some((record) => record.meta.debugRoute)) {
        if (!store.state.config.rules.debug) {
            next({ path: '/' });
        } else {
            next();
        }
    } else {
        next();
    }
});

router.beforeEach((to, from, next) => {
    if (to.path === '/client') {
        next({ path: '/' });
    } else {
        next();
    }
});

export default router;
